/**
 * Entry point for the Provet Online Booking React application.
 *
 * This file bootstraps the React application using dependency injection:
 * - DOMService abstracts DOM operations
 * - ReactRendererService abstracts React rendering
 * - BootstrapService orchestrates initialization
 *
 * **Dependency Injection Benefits:**
 * - Testable without actual DOM
 * - Can swap implementations easily
 * - Clear separation of concerns
 * - Better maintainability
 *
 * **Usage:**
 * This file is automatically loaded by the browser when the page loads.
 * It looks for elements with `data-react-component="ProvetBookingDialog"`
 * and renders React components into them.
 *
 * @example
 * ```html
 * <div data-react-component="ProvetBookingDialog" data-props='{"sessionId":"abc123"}'></div>
 * ```
 */

import { DEFAULT_BOOTSTRAP_CONFIG } from "./services/bootstrapService";
import {
  BrowserDOMService,
  ProvetBootstrapService,
  ProvetDOMUtilityService,
  ReactDOMRendererService
} from "./services/bootstrapServiceImpl";

// Create service instances using dependency injection
const domService = new BrowserDOMService();
const rendererService = new ReactDOMRendererService();
const bootstrapService = new ProvetBootstrapService(domService, rendererService);
const domUtilityService = new ProvetDOMUtilityService(domService);

/**
 * Toggles the consent/result view visibility.
 *
 * This function is exported for use by other parts of the application
 * that need to show/hide the booking confirmation.
 *
 * @example
 * ```typescript
 * import { toggleProvetResultView } from './index';
 *
 * // After successful booking
 * toggleProvetResultView();
 * ```
 */
export const toggleProvetResultView = () => {
  domUtilityService.toggleResultView();
};

/**
 * Initializes the Provet booking components when DOM is ready.
 *
 * This is the main entry point that:
 * 1. Waits for DOM to be loaded
 * 2. Finds all ProvetBookingDialog elements
 * 3. Renders React components into them
 */
function initializeProvetBooking() {
  bootstrapService.initialize(DEFAULT_BOOTSTRAP_CONFIG);
}

// Bootstrap when DOM is ready
domService.addEventListener("DOMContentLoaded", initializeProvetBooking);

// Export services for testing (optional)
export { bootstrapService, domService, domUtilityService, rendererService };
